/**
 * Multi-option selection (AB#522, rules set by the owner on AB#521).
 *
 * Wraps one selector group per product option and gates the Add button until
 * EVERY option has a value. Three products in the catalog have two options and
 * none has three: the pearl weave belt (Color x Size A1-A4, active) and the two
 * JJC "Extras" rashguards (Size x STATE, both draft).
 *
 * The rules, and why each one is here rather than being obvious:
 *
 *   1. Add stays disabled until every option is chosen, not just the first.
 *   2. The shake plus toast names the FIRST unselected group in DOM order, so
 *      the prompt is always about the thing the shopper must deal with next
 *      rather than the last thing in the list. "Pick a rank first", then
 *      "Pick a size first".
 *   3. An option with exactly one value arrives ALREADY CHOSEN, from the server.
 *      This reverses what this file used to say, and the reversal is the point of
 *      AB#549: the old rule refused to default anything, which read well and made
 *      one-size products unbuyable. The picker rendered Shopify's synthetic
 *      "Title: Default Title", left it unselected, and this gate then blocked
 *      every click on Add to Cart.
 *
 *      What that rule was protecting is kept: a genuine single-value option is
 *      still RENDERED, read-only, so the shopper still sees the dimension. Only
 *      the pointless click goes. A default-variant product renders no picker at
 *      all, so groups.length === 0 and this gate stands aside, which it already
 *      knew how to do.
 *
 *      The decision is the server's now, in snippets/mlr-needs-choice.liquid,
 *      because it is knowable at render time. Two scripts used to decide it
 *      between them and disagree.
 *   4. The gate is structural: it counts the groups actually rendered. It does
 *      not consult option_style or a handle, because both can drift and the DOM
 *      cannot. Both JJC products have a first option named "Size", so an
 *      option_style-driven gate would classify them as plain sizes, render one
 *      grid, leave Add enabled, and put an arbitrary US state in the cart.
 *
 * The button is disabled via aria-disabled and an intercepted click, NOT the
 * `disabled` attribute. A `disabled` button is removed from the tab order and
 * announces nothing, so a keyboard or screen-reader user gets silence instead of
 * the reason. This way it stays focusable and explains itself.
 */

const SHAKE_MS = 420;

export class MlrOptionSelect extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** @returns {HTMLElement[]} one element per rendered option group */
  get groups() {
    return Array.from(this.querySelectorAll('[data-mlr-option-group]'));
  }

  /** @returns {HTMLElement | null} */
  get addButton() {
    return this.querySelector('[data-mlr-add]');
  }

  /** The selected value of a group, or null when nothing is selected yet. */
  static selectionOf(group) {
    const chosen = group.querySelector('[data-mlr-option-value][aria-checked="true"]');
    return chosen ? chosen.getAttribute('data-mlr-option-value') : null;
  }

  /** Groups with no selection, in DOM order. */
  get unselectedGroups() {
    return this.groups.filter((g) => MlrOptionSelect.selectionOf(g) === null);
  }

  get isComplete() {
    const groups = this.groups;
    // No groups rendered at all means there is nothing to choose, which is a
    // single-variant product. Complete by definition rather than permanently
    // blocked: returning false here would make every one-size product unbuyable.
    return groups.length === 0 || this.unselectedGroups.length === 0;
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const value = target.closest('[data-mlr-option-value]');
    if (value instanceof HTMLElement && this.contains(value)) {
      if (value.hasAttribute('data-mlr-unavailable')) return;
      this.#select(value);
      return;
    }

    const add = target.closest('[data-mlr-add]');
    if (add && this.contains(add) && !this.isComplete) {
      // Intercept rather than rely on `disabled`, so the reason is announced.
      event.preventDefault();
      event.stopPropagation();
      this.#promptForFirstMissing();
    }
  };

  #onKeydown = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const value = target ? target.closest('[data-mlr-option-value]') : null;
    if (!value || !this.contains(value)) return;

    // Radio-group keyboard behaviour: arrows move between values in the group.
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (!keys.includes(event.key)) return;

    const group = value.closest('[data-mlr-option-group]');
    if (!group) return;
    const values = Array.from(group.querySelectorAll('[data-mlr-option-value]')).filter(
      (v) => !v.hasAttribute('data-mlr-unavailable')
    );
    const i = values.indexOf(value);
    if (i === -1) return;

    event.preventDefault();
    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const next = values[(i + (forward ? 1 : -1) + values.length) % values.length];
    if (next instanceof HTMLElement) {
      next.focus();
      this.#select(next);
    }
  };

  /** @param {HTMLElement} value */
  #select(value) {
    const group = value.closest('[data-mlr-option-group]');
    if (!group) return;

    for (const other of group.querySelectorAll('[data-mlr-option-value]')) {
      other.setAttribute('aria-checked', String(other === value));
      if (other instanceof HTMLElement) {
        // Only the selected value stays in the tab order, which is how a radio
        // group behaves: one stop, then arrows within it.
        other.tabIndex = other === value ? 0 : -1;
      }
    }

    group.removeAttribute('data-mlr-missing');
    this.#sync();

    this.dispatchEvent(
      new CustomEvent('mlr-option:change', {
        bubbles: true,
        detail: {
          option: group.getAttribute('data-mlr-option-group'),
          value: value.getAttribute('data-mlr-option-value'),
          complete: this.isComplete,
          selections: this.selections(),
        },
      })
    );
  }

  /** @returns {Record<string, string | null>} */
  selections() {
    /** @type {Record<string, string | null>} */
    const out = {};
    for (const g of this.groups) {
      out[g.getAttribute('data-mlr-option-group') ?? ''] = MlrOptionSelect.selectionOf(g);
    }
    return out;
  }

  #sync() {
    const add = this.addButton;
    if (!add) return;
    const complete = this.isComplete;
    add.setAttribute('aria-disabled', String(!complete));
    add.classList.toggle('mlr-add--waiting', !complete);
  }

  /**
   * Shake the first unselected group and say what it wants. The message names
   * the group, lowercased, so one rule covers "Pick a size first" and
   * "Pick a rank first" without a per-product string.
   */
  #promptForFirstMissing() {
    const [group] = this.unselectedGroups;
    if (!group) return;

    const label = (group.getAttribute('data-mlr-option-label') || 'option').toLowerCase();
    const message =
      group.getAttribute('data-mlr-option-prompt') || `Pick a ${label} first`;

    group.setAttribute('data-mlr-missing', '');
    window.setTimeout(() => group.removeAttribute('data-mlr-missing'), SHAKE_MS);

    const focusable = group.querySelector('[data-mlr-option-value]:not([data-mlr-unavailable])');
    if (focusable instanceof HTMLElement) focusable.focus();

    this.#toast(message);
  }

  /** @param {string} message */
  #toast(message) {
    // One live region per component, reused, so repeated prompts are announced
    // without stacking nodes. role=status is polite: it does not interrupt.
    let region = this.querySelector('[data-mlr-toast]');
    if (!region) {
      region = document.createElement('div');
      region.setAttribute('data-mlr-toast', '');
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.className = 'mlr-toast';
      this.appendChild(region);
    }
    // Clear first: setting the same text twice is not an update, so a second
    // identical prompt would be announced only once.
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
      region.classList.add('mlr-toast--visible');
    });
    window.clearTimeout(Number(region.dataset.timer));
    region.dataset.timer = String(
      window.setTimeout(() => region.classList.remove('mlr-toast--visible'), 2600)
    );
  }
}

if (!customElements.get('mlr-option-select')) {
  customElements.define('mlr-option-select', MlrOptionSelect);
}
